import { useEffect, useState } from "react";
import { logInteraction, type InteractionType } from "../actions";
import { IcCheck, IcClock, IcHome, IcPhone, IcWhatsapp } from "./icons";

/**
 * "תיעוד קשר" — the one thing the CRM asks a human to type.
 *
 * Until now the button on the person card was inert: it looked like the other
 * two beside it and did nothing at all, which is worse than not being there.
 *
 * The form is deliberately short. Somebody logging a call has just finished the
 * call and is not going to fill in eight fields, so it asks for the kind of
 * contact, one line of what happened, and nothing else unless they want to give
 * it. Date defaults to now and can be moved back — a call remembered in the
 * evening belongs on the day it happened.
 *
 * A note is treated differently from a conversation. "I should call him" is not
 * contact, and recording it as contact would make the person look attended to
 * when nobody has spoken to them — which is exactly the signal Today depends on.
 */

const KINDS: {
  type: InteractionType;
  channel: "phone" | "whatsapp" | "in_person" | "other";
  label: string;
  icon: React.ReactNode;
  tint: { bg: string; fg: string };
  /** Whether this counts as real contact for recency. */
  meaningful: boolean;
}[] = [
  {
    type: "call",
    channel: "phone",
    label: "שיחה",
    icon: <IcPhone />,
    tint: { bg: "var(--orange-soft)", fg: "var(--orange-2)" },
    meaningful: true,
  },
  {
    type: "whatsapp",
    channel: "whatsapp",
    label: "וואטסאפ",
    icon: <IcWhatsapp />,
    tint: { bg: "var(--green-soft)", fg: "var(--green)" },
    meaningful: true,
  },
  {
    type: "meeting",
    channel: "in_person",
    label: "פגישה",
    icon: <IcHome />,
    tint: { bg: "var(--slate-soft)", fg: "var(--slate)" },
    meaningful: true,
  },
  {
    type: "note",
    channel: "other",
    label: "הערה",
    icon: <IcClock />,
    tint: { bg: "var(--slate-soft)", fg: "var(--slate)" },
    meaningful: false,
  },
];

/** `datetime-local` wants local wall-clock time with no zone, which is not what
 *  `toISOString` produces. */
function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * When to be reminded.
 *
 * Offered as a few taps rather than a date picker, because the answer is almost
 * always one of these and a calendar is four interactions for something that
 * should be one. A specific date is still available for the case that needs it.
 *
 * Reminders land at 08:00 local: a follow-up that arrives at midnight has
 * already been missed by the time anybody looks at a phone.
 */
const REMIND_AT_HOUR = 8;

const WHENS: { id: string; label: string; days: number | null }[] = [
  { id: "none", label: "בלי משימה", days: null },
  { id: "tomorrow", label: "מחר", days: 1 },
  { id: "week", label: "בעוד שבוע", days: 7 },
  { id: "two_weeks", label: "בעוד שבועיים", days: 14 },
  { id: "month", label: "בעוד חודש", days: 30 },
  { id: "custom", label: "תאריך אחר", days: null },
];

function inDays(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(REMIND_AT_HOUR, 0, 0, 0);
  return d;
}

export function LogContactSheet({
  personId,
  personName,
  onClose,
}: {
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const [kind, setKind] = useState(KINDS[0]!);
  const [summary, setSummary] = useState("");
  const [details, setDetails] = useState("");
  const [when, setWhen] = useState(() => localInputValue(new Date()));
  const [remind, setRemind] = useState("none");
  const [remindNote, setRemindNote] = useState("");
  const [remindDate, setRemindDate] = useState(() => dateInputValue(inDays(7)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ reminded: boolean } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  async function save() {
    if (summary.trim() === "" && details.trim() === "") {
      setErr("כתבו בשורה אחת מה היה.");
      return;
    }
    // The reminder date is resolved here rather than on the server so the chip
    // the user tapped and the date that gets stored cannot disagree about which
    // timezone "tomorrow" is in.
    let followUp: { dueAt: string; note?: string } | undefined;
    if (remind !== "none") {
      const preset = WHENS.find((w) => w.id === remind);
      let due: Date;
      if (remind === "custom") {
        due = new Date(`${remindDate}T00:00:00`);
        due.setHours(REMIND_AT_HOUR, 0, 0, 0);
      } else {
        due = inDays(preset?.days ?? 7);
      }
      if (due.getTime() < Date.now()) {
        setErr("תאריך המשימה כבר עבר. בחרו תאריך אחר.");
        return;
      }
      followUp = {
        dueAt: due.toISOString(),
        note: remindNote.trim() || undefined,
      };
    }

    setBusy(true);
    setErr(null);
    try {
      await logInteraction({
        personId,
        type: kind.type,
        channel: kind.channel,
        direction: kind.type === "note" ? "none" : "outbound",
        summary: summary.trim(),
        details: details.trim() || undefined,
        // Sent as an instant so the server is not guessing the caller's zone.
        occurredAt: new Date(when).toISOString(),
        followUp,
      });
      setDone({ reminded: followUp !== undefined });
      // Left open for a moment so the confirmation is actually seen; the
      // timeline behind it has already updated.
      setTimeout(onClose, followUp ? 1400 : 900);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="תיעוד קשר">
        <div className="sheet-grip" />

        {done ? (
          <div className="qa-done">
            <div className="qa-done-mark">
              <IcCheck size={26} />
            </div>
            <h3>תועד</h3>
            <p>
              הוספנו את זה לציר הזמן של {personName}.
              {done.reminded && " המשימה תופיע במסך היום ובאינבוקס בתאריך שבחרתם."}
            </p>
          </div>
        ) : (
          <>
            <div className="sheet-head">
              <h2>תיעוד קשר · {personName}</h2>
            </div>

            <div className="form-field">
              <span className="form-label">מה היה</span>
              <div className="chips">
                {KINDS.map((k) => (
                  <button
                    key={k.type}
                    type="button"
                    className={kind.type === k.type ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setKind(k)}
                    disabled={busy}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              {!kind.meaningful && (
                <span className="form-hint">
                  הערה נשמרת בציר הזמן, אבל לא נחשבת „דיברנו”. ככה מסך היום לא יחשוב שכבר טיפלתם בו.
                </span>
              )}
            </div>

            <div className="form-field">
              <span className="form-label">בשורה אחת</span>
              <input
                className="form-input"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={
                  kind.type === "call"
                    ? "דיברנו על ההרשמה לקייטנה"
                    : kind.type === "note"
                      ? "לבדוק אם הוא צריך מזוזה"
                      : "על מה דיברתם?"
                }
                autoFocus
                disabled={busy}
                onKeyDown={(e) => e.key === "Enter" && !busy && save()}
              />
            </div>

            <div className="form-field">
              <span className="form-label">פרטים (לא חובה)</span>
              <textarea
                className="form-input"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                disabled={busy}
                style={{ resize: "vertical", minHeight: 68 }}
              />
            </div>

            <div className="form-field">
              <span className="form-label">מתי זה היה</span>
              <input
                className="form-input"
                type="datetime-local"
                value={when}
                max={localInputValue(new Date())}
                onChange={(e) => setWhen(e.target.value)}
                disabled={busy}
              />
              <span className="form-hint">ברירת המחדל היא עכשיו. אפשר לשנות לזמן מוקדם יותר.</span>
            </div>

            {/* The half the log was missing. A conversation that ended in a
                promise is not finished business, and a timeline entry records
                that it happened and then forgets what was owed. */}
            <div className="form-field">
              <span className="form-label">להזכיר לי לחזור לזה</span>
              <div className="chips">
                {WHENS.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    className={remind === w.id ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setRemind(w.id)}
                    disabled={busy}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            {remind !== "none" && (
              <>
                {remind === "custom" && (
                  <div className="form-field">
                    <span className="form-label">תאריך המשימה</span>
                    <input
                      className="form-input"
                      type="date"
                      value={remindDate}
                      min={dateInputValue(new Date())}
                      onChange={(e) => setRemindDate(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                )}

                <div className="form-field">
                  <span className="form-label">מה להזכיר</span>
                  <input
                    className="form-input"
                    value={remindNote}
                    onChange={(e) => setRemindNote(e.target.value)}
                    placeholder="לתאם שיעור בר מצווה לבנים"
                    disabled={busy}
                  />
                  <span className="form-hint">
                    אם תשאירו ריק, המשימה תקבל את מה שכתבתם למעלה. המשימה רשומה עליכם, לא על
                    מישהו אחר בצוות.
                  </span>
                </div>
              </>
            )}

            {err && <div className="form-error">{err}</div>}

            <div className="btn-row">
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? "שומרים…" : "שמרו"}
              </button>
              <button className="btn" onClick={onClose} disabled={busy}>
                ביטול
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
